/**
 * Connect-level design-axis plumbing (I-2510). The kit's axes come from two
 * account config keys: `look.design` holds what belongs to an appearance's
 * look (surface, density, actionlist, badges, page), the key `form` how forms look and
 * behave (input, reveal, inputicon, savestatus, caps, groups, ink). Table
 * zebra striping, the header fill and the cell alignment are no axes —
 * NxTable reads the key `table` itself. This module reads both documents against the
 * axis registry and pushes the merged state into the kit's design store
 * (→ `data-nx-*` on `<html>`); it never decides which values apply. The first
 * paint comes from the account config helper's boot cache of both keys.
 */
import {
  applyDesignToRoot,
  designAxes,
  isValidAxisValue,
  setDesign,
  useDesign,
  type DesignAxisName,
  type DesignState,
} from '@blpde/nx-ui';

export const DESIGN_AXIS_NAMES = Object.keys(designAxes) as DesignAxisName[];

/**
 * The axes an appearance's look carries (`look.design`): its surfaces and
 * spacing, the lists of a record's aside, the badge shape and the page.
 */
export const LOOK_DESIGN_AXES = [
  'surface',
  'density',
  'actionlist',
  'badges',
  'page',
] as const satisfies readonly DesignAxisName[];

/**
 * The axes of the form settings (the key `form`): the fields — input,
 * reveal, inputicon, savestatus — and the form around them — small headings
 * (caps), field groups (groups), the colour of the values (ink).
 */
export const FORM_AXES = [
  'input',
  'reveal',
  'inputicon',
  'savestatus',
  'caps',
  'groups',
  'ink',
] as const satisfies readonly DesignAxisName[];

export type LookDesignAxis = (typeof LOOK_DESIGN_AXES)[number];
export type FormAxis = (typeof FORM_AXES)[number];

/** The design of a look: `look.design`. */
export type LookDesign = Pick<DesignState, LookDesignAxis>;

/** The form settings: the key `form`. */
export type FormState = Pick<DesignState, FormAxis>;

/** Platform defaults for every axis, from the frontend/ui axis registry. */
export function defaultDesignState(): DesignState {
  const out = {} as Record<DesignAxisName, string>;
  for (const axis of DESIGN_AXIS_NAMES) out[axis] = designAxes[axis].default;
  return out as DesignState;
}

/** Validate a loose record against the axis registry; unknown/invalid → base. */
export function mergeDesignState(
  base: DesignState,
  patch: Partial<Record<string, string | undefined>> | undefined,
): DesignState {
  const out = { ...base } as Record<DesignAxisName, string>;
  if (patch) {
    for (const axis of DESIGN_AXIS_NAMES) {
      const value = patch[axis];
      if (typeof value === 'string' && isValidAxisValue(axis, value)) out[axis] = value;
    }
  }
  return out as DesignState;
}

/** The `axes` of `raw` that the registry accepts, over `base` (which holds exactly those axes). */
function pickAxes<A extends DesignAxisName>(
  axes: readonly A[],
  base: Pick<DesignState, A>,
  raw: unknown,
): Pick<DesignState, A> {
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out = {} as Record<A, string>;
  for (const axis of axes) {
    const value = record[axis];
    out[axis] = typeof value === 'string' && isValidAxisValue(axis, value) ? value : base[axis];
  }
  return out as Pick<DesignState, A>;
}

export function defaultLookDesign(): LookDesign {
  return pickAxes(LOOK_DESIGN_AXES, defaultDesignState(), undefined);
}

export function defaultFormState(): FormState {
  return pickAxes(FORM_AXES, defaultDesignState(), undefined);
}

/** `look.design` read against the registry: the look axes only, the rest ignored. */
export function lookDesignOf(raw: unknown, base: LookDesign = defaultLookDesign()): LookDesign {
  return pickAxes(LOOK_DESIGN_AXES, base, raw);
}

/** The key `form` read against the registry: the form axes only. */
export function formStateOf(raw: unknown, base: FormState = defaultFormState()): FormState {
  return pickAxes(FORM_AXES, base, raw);
}

/** Only the form axes `raw` names with a value the registry accepts. */
export function namedFormAxes(raw: unknown): Partial<FormState> {
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: Partial<Record<FormAxis, string>> = {};
  for (const axis of FORM_AXES) {
    const value = record[axis];
    if (typeof value === 'string' && isValidAxisValue(axis, value)) out[axis] = value;
  }
  return out as Partial<FormState>;
}

/** The state of every axis: the look's and the form's. */
export function designStateOf(look: LookDesign, form: FormState): DesignState {
  return { ...defaultDesignState(), ...look, ...form };
}

export function designStateEqual(a: DesignState, b: DesignState): boolean {
  return DESIGN_AXIS_NAMES.every((axis) => a[axis] === b[axis]);
}

/** Push a resolved axis state into the frontend/ui store (→ data-nx-* on <html>). */
export function applyDesignState(state: DesignState): void {
  for (const axis of DESIGN_AXIS_NAMES) {
    setDesign(axis, state[axis] as never);
  }
}

/** Re-export the reactive reader so views can reflect the effective axes. */
export function useShellSurface() {
  return useDesign();
}

export const supportedSurfaces = designAxes.surface.values;
export const supportedInputs = designAxes.input.values;
export const supportedReveals = designAxes.reveal.values;
export const supportedInputIcons = designAxes.inputicon.values;
export const supportedDensities = designAxes.density.values;
export const supportedSaveStatuses = designAxes.savestatus.values;
export const supportedCaps = designAxes.caps.values;
export const supportedGroups = designAxes.groups.values;
export const supportedInks = designAxes.ink.values;
export const supportedActionLists = designAxes.actionlist.values;
export const supportedBadges = designAxes.badges.values;
export const supportedPages = designAxes.page.values;

/** The two values of `table.zebra`. */
export const supportedZebras = ['on', 'off'] as const;
export type TableZebra = (typeof supportedZebras)[number];

/**
 * Call once at boot: bind the design store to <html>. The shell settings
 * (`settings.ts`) have already put the boot cache's look and form into it.
 */
export function initShellDesign(): void {
  applyDesignToRoot(document.documentElement);
}

export type { DesignAxisName, DesignState };
